'use strict';

var _ = require('lodash');
var Edit = require('../edit');
var Interface = require('./_Interface');
var TransformUtil = require('./TransformUtil');

/**
 * Transform Class
 *
 * This should not be used directly. It should be extended by child classes. The
 * necessary implementation for the children can be found in the `./_Interface`
 * file
 */

function Transform (Actions, Store) {
  this.Actions = Actions; // only needed when rendering
  this.Store = Store; // only needed when rendering
}

Transform.create = function () {
  var inst = Object.create(this.prototype);
  var obj = this.apply(inst, arguments);
  return Object(obj) === obj ? obj : inst;
};

/**
 * Extend method to be used by child classes
 * @param  {Object|Array} protoProps Object or Array of Object to extend prototype with
 * @param  {Object} staticProps
 */
Transform.extend = function (protoProps, staticProps) {
  var parent = this;
  var child;

  // Setup the child constructor
  if (protoProps && _.has(protoProps, 'constructor')) {
    child = protoProps.constructor;
  } else {
    child = function () {
      return parent.apply(this, arguments);
    };
  }

  _.extend(child, parent, staticProps);

  var Surrogate = function () {
    this.constructor = child;
  };

  Surrogate.prototype = parent.prototype;
  child.prototype = new Surrogate();

  if (protoProps) {
    if (_.isArray(protoProps)) {
      _.extend.apply(null, [child.prototype].concat(protoProps));
    } else {
      _.extend(child.prototype, protoProps);
    }
  }

  child.prototype.__super__ = parent.prototype;
  return child;
};

Transform.prototype = _.extend({

  _setLayerUID: function (layer) {
    if (!layer.$$layerUID) {
      layer.$$layerUID = TransformUtil.getGUID();
    }

    return layer;
  },

  format: function () {
    // TODO @andre Is this needed? Or is only formatLayer necessary?
  },

  /**
   * Parse a dataset in order to render it using angular-leaflet. This is where
   * the tranformation begins.
   *
   * @param  {Object|Array} dataset Single data model or array of data models
   * @return {Object} List of angular-leaflet geojson definition objects
   */
  parse: function (dataset, options) {

    var self = this;

    // Reset the map store
    // NOTE: This will need to be rethought if we end up needing to optimize by
    // only removing/adding layers instead of removing all layers and rerendering
    // them all
    this.Actions.ResetLayers.dispatch(this.collection);

    if (!dataset || (Array.isArray(dataset) && !dataset.length)) {
      return null;
    }

    if (Array.isArray(dataset)) {
      return _.reduce(dataset, function (geojson, data) {
        return _.extend(geojson, parseData(data, options));
      }, {});
    } else {
      return parseData(dataset, options);
    }

    function parseData (data, options) {
      // If `getCollection` is defined then we can assume that we are dealing
      // with a collection of layers
      if (self.getCollection) {
        var collection = self.getCollection(data);
        return _.reduce(collection, function (geojson, layer) {
          layer = self._setLayerUID(layer);
          geojson[layer.$$layerUID] = self.parseLayer(layer, options);
          return geojson;
        }, {});
      }
      // Not dealing with a collection, just a single layer
      else {
        var layer;
        var geojson = {};

        if (self.getModel) {
          layer = self.getModel(data);
        } else {
          layer = data;
        }

        // set the UID
        if (layer) {
          layer = self._setLayerUID(layer);
          geojson[layer.$$layerUID] = self.parseLayer(layer, options);
          return geojson;
        } else {
          return null;
        }
      }
    }
  },



  /**
   * Used to transform a wrapped layer back into its raw format
   */
  formatLayer: function (wLayer) {
    var geojsonKey = this.geojsonKey || 'data';

    var layer =  _.extend({}, wLayer.layer);
    var geoJSON = wLayer.leafletLayer.toGeoJSON();
    layer[geojsonKey].geometry = geoJSON.geometry;

    // reassign
    wLayer.layer = layer;

    return layer;
  },

  /**
   * Creates an object based on the Leaflet geojson options
   * @see http://leafletjs.com/reference.html#geojson
   */
  parseLayer: function (layer, options) {
    var geojsonKey = this.geojsonKey || 'data';
    var geojson = layer[geojsonKey];

    var styleFn = this.styleFn && this.styleFn.bind(this, layer);
    var pointToLayerFn = this.pointToLayerFn && this.pointToLayerFn.bind(
      this,
      layer,
      options
    );
    var onEachFeatureFn = this.onEachFeatureFn && this.onEachFeatureFn.bind(
      this,
      layer,
      this.Actions,
      this.Store
    );

    return {
      layer: layer, // raw layer data
      data: geojson, // geojson data
      onEachFeature: onEachFeatureFn,
      pointToLayer: pointToLayerFn,
      style: styleFn
    };
  },

  onEachFeatureFn: function (layer, Actions, Store, feature, leafletLayer) {
    var LayerClass;
    var layerType = this.getLeafletTypeFromLayer(layer);
    var styles = this.getLayerStateStyles(layer);

    if (this.supportsLayerType(layerType)) {
      LayerClass = Edit.getClassByType(layerType);

      var options = {
        styles: styles,
        isClickable: this.isLayerClickable(layer),
        isEditable: this.isLayerEditable(layer)
      };

      // Special ref for annotation layers.
      // TODO: Specify a common base of meta properties for all layer objects,
      // in case it needs to be added for more components

      if (layer.annotationId && layer.resource !== 'measurement') {
        var ref = 'annotation_layer-' + layer.annotationId + '-' + layer.id ;
        options._ref = ref;
        leafletLayer._ref = ref; // Add a _ref attribute to map with the source feature origin
      } else if (layer.resource === 'measurement') {
        var ref = 'measurement_layer-' + layer.id;
        options._ref = ref;
        leafletLayer._ref = ref; // Add a _ref attribute to map with the source feature origin
      }

      LayerClass.create(
        layer,
        leafletLayer,
        this.collection, // collection name
        options,
        Actions,
        Store
      );

      // TODO should this be moved to the renderer?
      Actions.RegisterLayer.dispatch(layer, this.collection);
    } else {
      throw new Error('The layer type `' + layerType + '` cannot be rendered');
    }
  },

  supportsLayerType: function (type) {
    var supported = ['circle', 'marker', 'polygon', 'polyline', 'rectangle', 'overlay', 'measurement'];
    return supported.indexOf(type) !== -1;
  }
});

module.exports = Transform;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/map/src/app/layer/transform/Transform.js